/**
 * Multi-Tenant Configuration System
 * 
 * This file defines all tenant-specific configurations including:
 * - Brand identity (logo, app name, favicon)
 * - Color palette
 * - Theme customizations
 * 
 * To add a new tenant:
 * 1. Add logo files to /public/assets/logos/{tenant}/
 * 2. Add tenant configuration below
 * 3. Update VITE_TENANT in system-config.json
 */

export interface TenantBrandConfig {
  tenantId: string;
  appName: string;
  logo: string;
  favicon: string;
  colors: {
    primary: string;
    secondary: string;
  };
}

/**
 * Tenant Configurations
 * Each tenant has isolated brand assets and color scheme
 */
const TENANT_CONFIGS: Record<string, TenantBrandConfig> = {
  hicas: {
    tenantId: "hicas",
    appName: "Hicas",
    logo: "/assets/logos/hicas/logo.svg",
    favicon: "/assets/logos/hicas/logo.svg",
    colors: {
      primary: "#0D1C36",
      secondary: "#56a97a",
    },
  },
  hawee: {
    tenantId: "hawee",
    appName: "Hawee",
    logo: "/assets/logos/hawee/logo.svg",
    favicon: "/assets/logos/hawee/logo.svg",
    colors: {
      primary: "#0D1C36",
      secondary: "#56a97a",
    },
  },
  cxv: {
    tenantId: "cxv",
    appName: "CXV Lakehouse",
    logo: "/assets/logos/cxv/logo.png",
    favicon: "/assets/logos/cxv/logo.png",
    colors: {
      primary: "#0D1C36",
      secondary: "#56a97a",
    },
  },
};

/**
 * Get tenant configuration by tenant ID
 * @param tenantId - The tenant identifier (hicas, hawee, cxv, etc.)
 * @returns Tenant brand configuration
 * @throws Error if tenant not found (fail-fast to prevent brand confusion)
 */

export const BANNERS_BY_TENANT = {
    hicas: [
        {
            image: 'https://admin.hicas.vn/uploads/banner_home_1_36e1b88e1d.png',
            title: 'BIM CDE quản lý hệ thống thông tin xây dựng',
            subTitle: '',
            link: 'https://hicas.vn/',
            linkText: 'Tìm hiểu thêm',
        },
        {
            image:
                'https://admin.hicas.vn/uploads/Wisenet_Road_Press_Release_Image_USE_v2_2021_10_07_6103b0b2c6.jpg',
            title: 'Giải pháp AI phân tích dữ liệu camera',
            subTitle: '',
            link: 'https://hicas.vn/',
            linkText: 'Tìm hiểu thêm',
        },
    ],

    hawee: [
        {
            image: 'https://hawee.hicas.vn/imgs/slide1.webp',
            title: 'ĐỘI NGŨ NHÂN SỰ CHUYÊN SÂU',
            subTitle: 'LĨNH VỰC CƠ ĐIỆN TỬ',
            link: 'https://me.hawee.com.vn/',
            linkText: 'Tìm hiểu thêm',
        },
        {
            image: 'https://hawee.hicas.vn/imgs/slide2.webp',
            title: 'GIẢI PHÁP CÔNG NGHỆ TIÊN TIẾN',
            subTitle: 'PHÙ HỢP MỌI QUY MÔ DỰ ÁN',
            link: 'https://me.hawee.com.vn/',
            linkText: 'Khám phá',
        },
    ],

    cxv: [
        {
            image: 'https://admin.hicas.vn/uploads/banner_home_1_36e1b88e1d.png',
            title: 'BIM CDE quản lý hệ thống thông tin xây dựng',
            subTitle: '',
            link: 'https://hicas.vn/',
            linkText: 'Tìm hiểu thêm',
        },
        {
            image:
                'https://admin.hicas.vn/uploads/Wisenet_Road_Press_Release_Image_USE_v2_2021_10_07_6103b0b2c6.jpg',
            title: 'Giải pháp AI phân tích dữ liệu camera',
            subTitle: '',
            link: 'https://hicas.vn/',
            linkText: 'Tìm hiểu thêm',
        },
    ],
};

export function getTenantConfig(tenantId: string): TenantBrandConfig {
  const config = TENANT_CONFIGS[tenantId?.toLowerCase()];
  
  if (!config) {
    const availableTenants = Object.keys(TENANT_CONFIGS).join(", ");
    throw new Error(
      `[TENANT CONFIG ERROR] Tenant "${tenantId}" not found. ` +
      `Available tenants: ${availableTenants}. ` +
      `Please check VITE_TENANT in system-config.json`
    );
  }
  
  console.log(`[Tenant Config] Loaded configuration for: ${config.appName}`);
  return config;
}

/**
 * Get default tenant (fallback)
 */
export function getDefaultTenant(): string {
  return "hicas";
}

/**
 * List all available tenants
 */
export function getAvailableTenants(): string[] {
  return Object.keys(TENANT_CONFIGS);
}
